import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
	MagnifyingGlassIcon,
	FunnelIcon,
	ArrowDownTrayIcon,
	CheckCircleIcon,
	XCircleIcon,
	ClockIcon,
	ChevronUpIcon,
	ChevronDownIcon
} from '@heroicons/react/24/outline';

import { API_URL } from '../../config/api';

const AuditLog = () => {
	const [logs, setLogs] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	
	// Filtros
	const [searchTerm, setSearchTerm] = useState('');
	const [filterAccion, setFilterAccion] = useState('');
	const [filterModelo, setFilterModelo] = useState('');
	const [filterExitoso, setFilterExitoso] = useState('');
	const [fechaDesde, setFechaDesde] = useState('');
	const [fechaHasta, setFechaHasta] = useState('');
	
	// Paginación del backend
	const [pagination, setPagination] = useState({
		count: 0,
		next: null,
		previous: null,
		page_size: 10,
		page_size_options: [10, 15, 20, 50],
		current_page: 1,
		total_pages: 1
	});
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	
	// Estado para mostrar/ocultar filtros
	const [filtrosVisibles, setFiltrosVisibles] = useState(true);

	useEffect(() => {
		loadLogs();
	}, [currentPage, pageSize, filterAccion, filterModelo, filterExitoso, fechaDesde, fechaHasta, searchTerm]);

	const loadLogs = async () => {
		setLoading(true);
		try {
			const params = new URLSearchParams();
			if (searchTerm) params.append('busqueda', searchTerm);
			if (filterAccion) params.append('accion', filterAccion);
			if (filterModelo) params.append('modelo', filterModelo);
			if (filterExitoso) params.append('exitoso', filterExitoso);
			if (fechaDesde) params.append('fecha_desde', fechaDesde);
			if (fechaHasta) params.append('fecha_hasta', fechaHasta);
			
			// Agregar parámetros de paginación
			params.append('page', currentPage);
			params.append('page_size', pageSize);
			
			const response = await axios.get(`${API_URL}/api/audit/logs/?${params.toString()}`);
			
			// Manejar respuesta paginada del backend
			if (response.data.results) {
				setLogs(response.data.results);
				setPagination({
					count: response.data.count || 0,
					next: response.data.next,
					previous: response.data.previous,
					page_size: response.data.page_size || pageSize,
					page_size_options: response.data.page_size_options || [10, 15, 20, 50],
					current_page: response.data.current_page || currentPage,
					total_pages: response.data.total_pages || 1
				});
			} else {
				// Fallback si no viene paginado
				setLogs(Array.isArray(response.data) ? response.data : []);
			}
			setError('');
		} catch (err) {
			setError('Error al cargar registros de auditoría');
			console.error('Error cargando logs:', err);
		} finally {
			setLoading(false);
		}
	};

	const getAccionColor = (accion) => {
		const colors = {
			create: 'bg-green-100 text-green-800',
			read: 'bg-blue-100 text-blue-800',
			update: 'bg-yellow-100 text-yellow-800',
			delete: 'bg-red-100 text-red-800',
			export: 'bg-purple-100 text-purple-800',
			process: 'bg-indigo-100 text-indigo-800',
			login: 'bg-teal-100 text-teal-800',
			logout: 'bg-gray-100 text-gray-800',
		};
		return colors[accion] || 'bg-gray-100 text-gray-800';
	};

	const formatDate = (dateString) => {
		if (!dateString) return 'N/A';
		const date = new Date(dateString);
		return date.toLocaleString('es-DO', {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit'
		});
	};

	const clearFilters = () => {
		setSearchTerm('');
		setFilterAccion('');
		setFilterModelo('');
		setFilterExitoso('');
		setFechaDesde('');
		setFechaHasta('');
		setCurrentPage(1);
	};

	const hasActiveFilters = searchTerm || filterAccion || filterModelo || filterExitoso || fechaDesde || fechaHasta;

	return (
		<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
			{/* Header */}
			<div className="mb-6">
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
					<div>
						<h1 className="text-3xl font-bold text-gray-900">Registros de Auditoría</h1>
						<p className="mt-2 text-sm text-gray-600">
							Historial completo de todas las acciones realizadas en el sistema
						</p>
					</div>
				</div>
			</div>

			{/* Filtros - Compactos y colapsables */}
			<div className="bg-white rounded-lg shadow mb-6">
				<div className="flex items-center justify-between p-3 border-b border-gray-200">
					<h2 className="text-sm font-semibold text-gray-900">Filtros</h2>
					<div className="flex items-center gap-2">
						{hasActiveFilters && (
							<button
								onClick={clearFilters}
								className="text-xs text-primary hover:text-primary/80 font-medium px-2 py-1"
							>
								Limpiar
							</button>
						)}
						<button
							onClick={() => setFiltrosVisibles(!filtrosVisibles)}
							className="text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1 px-2 py-1"
						>
							{filtrosVisibles ? (
								<>
									<ChevronUpIcon className="h-4 w-4" />
									Ocultar
								</>
							) : (
								<>
									<ChevronDownIcon className="h-4 w-4" />
									Mostrar
								</>
							)}
						</button>
					</div>
				</div>
				
				{filtrosVisibles && (
					<div className="p-3">
						<div className="flex flex-wrap items-end gap-2">
							{/* Búsqueda */}
							<div className="flex-1 min-w-[200px]">
								<label className="block text-xs font-medium text-gray-700 mb-1">
									Buscar
								</label>
								<div className="relative">
									<MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
									<input
										type="text"
										placeholder="Buscar en registros..."
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
										className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
									/>
								</div>
							</div>

							{/* Filtro por acción */}
							<div className="min-w-[140px]">
								<label className="block text-xs font-medium text-gray-700 mb-1">
									Acción
								</label>
								<select
									value={filterAccion}
									onChange={(e) => setFilterAccion(e.target.value)}
									className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
								>
									<option value="">Todas</option>
									<option value="create">Crear</option>
									<option value="read">Leer</option>
									<option value="update">Actualizar</option>
									<option value="delete">Eliminar</option>
									<option value="export">Exportar</option>
									<option value="process">Procesar</option>
									<option value="login">Iniciar Sesión</option>
									<option value="logout">Cerrar Sesión</option>
									<option value="other">Otro</option>
								</select>
							</div>

							{/* Filtro por modelo */}
							<div className="min-w-[140px]">
								<label className="block text-xs font-medium text-gray-700 mb-1">
									Modelo
								</label>
								<select
									value={filterModelo}
									onChange={(e) => setFilterModelo(e.target.value)}
									className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
								>
									<option value="">Todos</option>
									<option value="FormularioDGII">Formulario DGII</option>
									<option value="FacturaAdjunta">Factura Adjunta</option>
									<option value="FacturaExtraida">Factura Extraída</option>
									<option value="User">Usuario</option>
									<option value="UserProfile">Perfil de Usuario</option>
								</select>
							</div>

							{/* Filtro por éxito */}
							<div className="min-w-[120px]">
								<label className="block text-xs font-medium text-gray-700 mb-1">
									Estado
								</label>
								<select
									value={filterExitoso}
									onChange={(e) => setFilterExitoso(e.target.value)}
									className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
								>
									<option value="">Todos</option>
									<option value="true">Exitosos</option>
									<option value="false">Fallidos</option>
								</select>
							</div>

							{/* Fecha desde */}
							<div className="min-w-[160px]">
								<label className="block text-xs font-medium text-gray-700 mb-1">
									Fecha Desde
								</label>
								<input
									type="datetime-local"
									value={fechaDesde}
									onChange={(e) => setFechaDesde(e.target.value)}
									className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
								/>
							</div>

							{/* Fecha hasta */}
							<div className="min-w-[160px]">
								<label className="block text-xs font-medium text-gray-700 mb-1">
									Fecha Hasta
								</label>
								<input
									type="datetime-local"
									value={fechaHasta}
									onChange={(e) => setFechaHasta(e.target.value)}
									className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
								/>
							</div>
						</div>
					</div>
				)}
			</div>

			{/* Tabla de registros */}
			{loading ? (
				<div className="text-center py-12">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
					<p className="mt-4 text-gray-600">Cargando registros...</p>
				</div>
			) : error ? (
				<div className="bg-red-50 border border-red-200 rounded-lg p-4">
					<p className="text-red-800">{error}</p>
				</div>
			) : (
				<div className="bg-white rounded-lg shadow overflow-hidden">
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-gray-200">
							<thead className="bg-gray-50">
								<tr>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Fecha/Hora
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Usuario
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Acción
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Modelo
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										ID Objeto
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Descripción
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										IP
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Estado
									</th>
								</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200">
								{logs.length === 0 ? (
									<tr>
										<td colSpan="8" className="px-6 py-12 text-center text-gray-500">
											No se encontraron registros de auditoría
										</td>
									</tr>
								) : (
									logs.map((log) => (
										<tr key={log.id} className="hover:bg-gray-50">
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
												{formatDate(log.fecha_accion)}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
												{log.usuario_nombre || 'Anónimo'}
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												<span className={`px-2 py-1 rounded text-xs font-medium ${getAccionColor(log.accion)}`}>
													{log.accion_display}
												</span>
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
												{log.modelo_display || log.modelo}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												{log.objeto_id || 'N/A'}
											</td>
											<td className="px-6 py-4 text-sm text-gray-900">
												{log.descripcion || '-'}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												{log.ip_address || 'N/A'}
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												{log.exitoso ? (
													<CheckCircleIcon className="h-5 w-5 text-green-500" />
												) : (
													<XCircleIcon className="h-5 w-5 text-red-500" />
												)}
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>

					{/* Paginación del backend */}
					{pagination.count > 0 && (
						<div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 px-6 py-4 border-t border-gray-200 bg-gray-50">
							<div className="flex items-center gap-3">
								<span className="text-sm text-gray-600">
									Mostrando {((pagination.current_page - 1) * pagination.page_size) + 1} - {Math.min(pagination.current_page * pagination.page_size, pagination.count)} de {pagination.count} registros
								</span>
								<select
									value={pageSize}
									onChange={(e) => {
										setPageSize(Number(e.target.value));
										setCurrentPage(1);
									}}
									className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
								>
									{pagination.page_size_options.map(size => (
										<option key={size} value={size}>{size} por página</option>
									))}
								</select>
							</div>
							<div className="flex items-center gap-2">
								<button
									onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
									disabled={!pagination.previous || currentPage === 1}
									className="px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
								>
									Anterior
								</button>
								<span className="px-3 py-1.5 text-sm text-gray-700">
									Página {pagination.current_page} de {pagination.total_pages}
								</span>
								<button
									onClick={() => setCurrentPage(prev => prev + 1)}
									disabled={!pagination.next || currentPage >= pagination.total_pages}
									className="px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
								>
									Siguiente
								</button>
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default AuditLog;
